export const OUTGOING_INVOICES_SORTER_FORM_DEFAULT_PARAMS = Object.freeze({});
export const OUTGOING_INVOICES_FILTER_FORM_DEFAULT_PARAMS = Object.freeze({
  supplyDate: undefined,
  // The book's issue and acceptance dates, offered in the panel and in the
  // column headers. Listed so "clear all filters" clears them too.
  documentDate: undefined,
  decidedAt: undefined,
  dueDate: undefined,
  deliveryDate: undefined,
  products: undefined,
  createdAt: undefined,
  updatedAt: undefined,
  productsService: undefined,
  productText: undefined,
  // The printed mark, offered in this panel.
  printed: undefined,
  // Paid / partly paid from the bank statements, offered in this panel.
  payment: undefined,
});
export const OUTGOING_INVOICES_COLUMNS_DEFAULT_PARAMS = Object.freeze({
  // filters
  documentNumber: undefined,
  salesInvoiceId: undefined,
  total: undefined,
  customerName: undefined,
  status: undefined,
  internalReferenceNumber: undefined,
  project: undefined,
  customerVat: undefined,
  transactionDescription: undefined,
  journalPeriod: undefined,
  createdBy: undefined,
  // The payment column's funnel.
  payment: undefined,
});
export const OUTGOING_INVOICES_DEFAULT_PARAMS = Object.freeze({
  // defaults
  offset: '1',
  // Only invoices with a message waiting for the person looking.
  unreadMessages: undefined,
  // 15, the same as the incoming list: a small company's whole month fits on
  // one page.
  limit: '15',
  // filters
  productsService: [],
  productText: undefined,
  // sorters
  sortDocumentNumber: undefined,
  sortSalesInvoiceId: undefined,
  sortSupplyDate: undefined,
  sortTotal: undefined,
  sortCustomerName: undefined,
  sortInternalReferenceNumber: undefined,
  sortStatus: undefined,
  sortUpdatedAt: undefined,
  ...OUTGOING_INVOICES_SORTER_FORM_DEFAULT_PARAMS,
  ...OUTGOING_INVOICES_FILTER_FORM_DEFAULT_PARAMS,
  ...OUTGOING_INVOICES_COLUMNS_DEFAULT_PARAMS,
});
export const OUTGOING_INVOICES_FIELD_MAP = {
  documentNumber: {
    path: 'referenceId.documentNumber',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.documentNumber',
    filterKey: 'documentNumber',
  },
  salesInvoiceId: {
    path: 'referenceId.salesInvoiceId',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.salesInvoiceId',
    filterKey: 'salesInvoiceId',
  },
  total: {
    path: 'totalAmount',
    isRef: false,
    type: 'number',
    sortKey: 'totalAmount',
    filterKey: 'totalAmount',
  },
  customerName: {
    path: 'referenceId.customerName',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.customerName',
    filterKey: 'customerName',
  },
  status: { path: 'status', isRef: false, type: 'array', sortKey: 'status' },
  supplyDate: {
    path: 'referenceId.supplyDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.supplyDate',
  },
  updatedAt: {
    path: 'updatedAt',
    isRef: false,
    type: 'date',
    sortKey: 'updatedAt',
  },
  // The column that says who entered the invoice sorts by when it was entered;
  // the name itself lives on another collection and cannot be sorted here.
  createdAt: {
    path: 'createdAt',
    isRef: false,
    type: 'date',
    sortKey: 'createdAt',
  },
  // Searched by name. The invoice stores a user id, so the server resolves what
  // was typed into the people it names — the same path the incoming book takes
  // for who accepted an invoice. No `sortKey`: sorting belongs to `createdAt`
  // above, and declaring one here would put arrows on a column that cannot
  // honour them.
  // Whether we have printed it yet. On the root invoice, so not a reference
  // field; `array` because the question is "printed or not" — the server turns
  // `printed` / `__none__` into a presence test.
  // Paid / partly paid, from the bank statements — see the incoming twin.
  payment: {
    path: 'referenceId.payment.status',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.payment.status',
    filterKey: 'payment.status',
  },
  printed: {
    path: 'printed.at',
    isRef: false,
    type: 'array',
    sortKey: 'printed.at',
  },
  createdBy: {
    path: 'createdBy',
    isRef: false,
    type: 'text',
    filterKey: 'createdBy',
  },
  productsService: {
    path: 'referenceId.products.serviceProduct',
    isRef: true,
    type: 'array',
    filterKey: 'products.serviceProduct',
  },
  productText: {
    path: 'referenceId.products.description',
    isRef: true,
    type: 'text',
    filterKey: 'products.description',
  },
  internalReferenceNumber: {
    path: 'referenceId.internalReferenceNumber',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.internalReferenceNumber',
    filterKey: 'internalReferenceNumber',
  },
  // ── The outgoing book ─────────────────────────────────────────────────────
  //
  // Only filled for a company with `salesJournalEnabled`, and only the book
  // screen shows them. Declared the same way as on the incoming map, for the
  // same reason: one entry gives the filter and the sorter together.
  project: {
    path: 'referenceId.project',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.project',
    filterKey: 'project',
  },
  transactionDescription: {
    path: 'referenceId.transactionDescription',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.transactionDescription',
    filterKey: 'transactionDescription',
  },
  journalPeriod: {
    path: 'referenceId.journalPeriod',
    isRef: true,
    type: 'default',
    sortKey: 'referenceId.journalPeriod',
    filterKey: 'journalPeriod',
  },
  documentDate: {
    path: 'referenceId.documentDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.documentDate',
    filterKey: 'documentDate',
  },
  // The date of supply, which decides the book month.
  deliveryDate: {
    path: 'referenceId.deliveryDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.deliveryDate',
    filterKey: 'deliveryDate',
  },
  // When the buyer accepted or refused. A real Date, unlike the string dates
  // beside it — the server filters it through the same branch as the incoming
  // acceptance date (`instantFields` in `coerceRefFiltersHelper`).
  decidedAt: {
    path: 'referenceId.decidedAt',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.decidedAt',
    filterKey: 'decidedAt',
  },
  customerVat: {
    path: 'referenceId.customerVat',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.customerVat',
    filterKey: 'customerVat',
  },
  // Gross, before prepayments — not `total`.
  totalWithVat: {
    path: 'referenceId.totalWithVat',
    isRef: true,
    type: 'number',
    sortKey: 'referenceId.totalWithVat',
    sortOnly: true,
  },
  vatAmount: {
    path: 'referenceId.vatAmount',
    isRef: true,
    type: 'number',
    sortKey: 'referenceId.vatAmount',
    sortOnly: true,
  },
};
